import React, { useState, useEffect } from 'react';
import loadingimg from './Images/loading.gif';
import { useNavigate } from 'react-router-dom';
import { databaseURL } from './firebase';
import { ALL_PLAN_FEATURES, FREE_TRIAL_PLAN, PAID_PLANS, normalizePlanPrices } from './plans';

function ActivationPlan() {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [expandedSchool, setExpandedSchool] = useState(null); // State to manage expanded school
  const [deactivatingUsers, setDeactivatingUsers] = useState([]);
  const [showExpiryPlanList, setShowExpiryPlanList] = useState(false);
  const [passkey, setPasskey] = useState('');
  const [searchQuery, setSearchQuery] = useState(""); // State for search functionality
  const navigate = useNavigate();

  useEffect(() => {
    fetchdata();
  }, []);


  useEffect(() => {
    const today = new Date();
    const fiveDaysLater = new Date(today.getTime() + 5 * 24 * 60 * 60 * 1000);
    const deactivatingUsersList = schools.filter((school) => {
      const deactivationDate = formData[school.udiseNumber]?.deactivationDate;
      if (deactivationDate) {
        const deactivationDateObject = new Date(deactivationDate);
        return deactivationDateObject >= today && deactivationDateObject <= fiveDaysLater;
      }
      return false;
    });
    setDeactivatingUsers(deactivatingUsersList);
  }, [schools, formData]);

  // State to track the current sorting column and order
  const [sort, setSort] = useState({
    registrationDate: null,
    userStatus: null,
    schoolName: null,
    udiseNumber: null,
  });

  const handleSort = (column) => {
    setSort((prevSort) => {
      const newOrder = prevSort[column] === 'asc' ? 'desc' : 'asc';
      return {
        registrationDate: null,
        userStatus: null,
        schoolName: null,
        udiseNumber: null,
        [column]: newOrder,
      };
    });
  };

  const sortedSchools = schools.sort((a, b) => {
    if (sort.registrationDate) {
      const dateA = a.registerDate === '-' ? 0 : Date.parse(a.registerDate);
      const dateB = b.registerDate === '-' ? 0 : Date.parse(b.registerDate);
      if (dateA !== dateB) {
        return sort.registrationDate === 'asc' ? dateA - dateB : dateB - dateA;
      }
    }

    if (sort.userStatus) {
      const statusOrder = { Active: 1, Deactive: 2, '-': 3 };
      const statusA = statusOrder[a.userStatus] || 3;
      const statusB = statusOrder[b.userStatus] || 3;
      if (statusA !== statusB) {
        return sort.userStatus === 'asc' ? statusA - statusB : statusB - statusA;
      }
    }

    if (sort.schoolName) {
      if (a.schoolName !== b.schoolName) {
        return sort.schoolName === 'asc'
          ? a.schoolName.localeCompare(b.schoolName)
          : b.schoolName.localeCompare(a.schoolName);
      }
    }

    if (sort.udiseNumber) {
      if (a.udiseNumber !== b.udiseNumber) {
        return sort.udiseNumber === 'asc'
          ? parseInt(a.udiseNumber) - parseInt(b.udiseNumber)
          : parseInt(b.udiseNumber) - parseInt(a.udiseNumber);
      }
    }
    return 0; // Maintain original order
  });

  // Filter schools based on search query
  const filteredSchools = sortedSchools.filter((school) => {
    const query = searchQuery.toLowerCase();
    return (
      (school.schoolName || '').toString().toLowerCase().includes(query) ||
      (school.udiseNumber || '').toString().toLowerCase().includes(query) ||
      (school.mobileNumber || '').toString().toLowerCase().includes(query) ||
      (school.userStatus || '').toString().toLowerCase().includes(query) ||
      (school.registerDate || '').toString().toLowerCase().includes(query)
    );
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [plansData, setPlansData] = useState(() => normalizePlanPrices());


  const handlePackageSelection = (udiseNumber, planName) => {
    try {
      const selectedPlan = PAID_PLANS.find((plan) => plan.name === planName);
      if (!selectedPlan) {
        throw new Error(`Unknown subscription plan: ${planName}`);
      }

      const activationDate = new Date();
      const deactivationDate = new Date(activationDate);
      deactivationDate.setFullYear(deactivationDate.getFullYear() + 1); // 1 year duration

      setFormData((previousFormData) => ({
        ...previousFormData,
        [udiseNumber]: {
          ...previousFormData[udiseNumber],
          activationDate: activationDate.toISOString().split('T')[0],
          deactivationDate: deactivationDate.toISOString().split('T')[0],
          packActivated: 'purchase',
          planName: selectedPlan.name,
          planPrice: plansData[selectedPlan.name]?.mrp ?? selectedPlan.price,
          studentLimit: selectedPlan.studentLimit,
          studentLimitText: `Up to ${selectedPlan.studentLimit.toLocaleString()} students`,
          features: ALL_PLAN_FEATURES,
          includedFeatures: selectedPlan.includedFeatures,
          lockedModule: {}
        },
      }));
    } catch (error) {
      console.error(error);
    }
  };


  const fetchPlansData = async () => {
    try {
      const adminResponse = await fetch(`${databaseURL}/Admin/purchesPlan.json`);
      if (!adminResponse.ok) throw new Error('Failed to fetch default subscription plans');
      const adminPlanData = await adminResponse.json();

      const schoolResponse = await fetch(`${databaseURL}/Admin/${expandedSchool}/purchesPlan.json`);
      if (!schoolResponse.ok) throw new Error('Failed to fetch school subscription plans');
      const schoolPlanData = await schoolResponse.json();

      setPlansData(normalizePlanPrices({
        ...(adminPlanData || {}),
        ...(schoolPlanData || {})
      }));
    } catch (error) {
      console.error(error);
      try {
        const fallbackResponse = await fetch(`${databaseURL}/Admin/purchesPlan.json`);
        if (!fallbackResponse.ok) throw new Error('Failed to fetch default subscription plans');
        const fallbackData = await fallbackResponse.json();
        setPlansData(normalizePlanPrices(fallbackData));
      } catch (fallbackError) {
        console.error("Fallback fetch also failed:", fallbackError);
      }
    }
  };
  useEffect(() => {
    if (isModalOpen && expandedSchool) {
      fetchPlansData(expandedSchool);
    }
  }, [isModalOpen, expandedSchool]);

  // Define the Modal component
  const Modal = ({ children }) => {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', width: '400px', boxShadow: '0px 10px 25px rgba(0,0,0,0.3)' }}>
          {children}
        </div>
      </div>
    );
  };

  const handleFreePackageSelection = (udiseNumber) => {
    try {
      const activationDate = new Date();
      const deactivationDate = new Date(activationDate);
      deactivationDate.setDate(deactivationDate.getDate() + FREE_TRIAL_PLAN.durationDays);

      setFormData({
        ...formData,
        [udiseNumber]: {
          ...formData[udiseNumber],
          activationDate: activationDate.toISOString().split('T')[0],
          deactivationDate: deactivationDate.toISOString().split('T')[0],
          planName: FREE_TRIAL_PLAN.name,
          planPrice: FREE_TRIAL_PLAN.price,
          studentLimit: FREE_TRIAL_PLAN.studentLimit,
          studentLimitText: `Up to ${FREE_TRIAL_PLAN.studentLimit} students`,
          features: ALL_PLAN_FEATURES,
          includedFeatures: FREE_TRIAL_PLAN.includedFeatures,
          lockedModule: {}
        },
      });
    } catch (error) {
      console.error(error);
      // Handle the error here, e.g. display an error message to the user
    }
  };
  //898+?

  const fetchdata = async () => {
    try {
      const response = await fetch(`${databaseURL}/Admin.json`);
      const data = await response.json();
      console.log("data", data);

      const schoolsData = Object.keys(data).map((udiseNumber) => {
        const schoolData = data[udiseNumber];

        return {
          id: udiseNumber,
          schoolName: schoolData.schoolName || '-',
          udiseNumber: udiseNumber,
          mobileNumber: schoolData.mobileNumber || '-',
          userStatus: schoolData.userStatus || '-',
          registerDate: schoolData.registerDate || '-',
        };
      });

      const adminData = await fetch(`${databaseURL}/Admin.json`)
        .then(response => response.json())
        .then(adminData => {
          return Object.keys(adminData).map((udiseNumber) => {
            return {
              ...adminData[udiseNumber],
              udiseNumber: udiseNumber,
            };
          });
        });

      const formData = adminData.reduce((acc, curr) => {
        acc[curr.udiseNumber] = curr;
        return acc;
      }, {});

      // Fetch the passkey
      const passkeyResponse = await fetch(`${databaseURL}/Admin/passkey.json`);
      const passkeyData = await passkeyResponse.json();
      setPasskey(passkeyData); // Assuming passkeyData is a string

      setSchools(schoolsData);
      setFormData(formData);
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Run the check immediately on page load
    if (schools && schools.length > 0 && formData) {
      checkDeactivationDates();
    }
  }, [schools, formData]); // Only run when these dependencies change



  const checkDeactivationDates = () => {
    const today = new Date().toISOString().split('T')[0]; // Current date in YYYY-MM-DD format

    schools.forEach((school) => {
      const deactivationDate = formData[school.udiseNumber]?.deactivationDate;

      // If the deactivationDate is equal to or earlier than today's date, deactivate user
      if (
        deactivationDate &&
        new Date(deactivationDate) <= new Date(today) && // Compare dates
        school.userStatus === 'Active'
      ) {
        // Check if the school was just activated
        const wasJustActivated = formData[school.udiseNumber]?.activationDate === today;
      }
    });
  };

  const handleToggleStatus = async (udiseNumber) => {
    const schoolToUpdate = schools.find(school => school.udiseNumber === udiseNumber);

    if (schoolToUpdate) {
      const enteredPasskey = prompt("Please enter the passkey to change the status:");

      if (enteredPasskey === passkey) {
        const salePersonName = formData[schoolToUpdate.udiseNumber]?.salePersonName;
        const paidAmount = formData[schoolToUpdate.udiseNumber]?.paidAmount;
        const finalPrize = formData[schoolToUpdate.udiseNumber]?.finalPrize;
        const activationDate = formData[schoolToUpdate.udiseNumber]?.activationDate;
        const deactivationDate = formData[schoolToUpdate.udiseNumber]?.deactivationDate;
        const packActivated = formData[schoolToUpdate.udiseNumber]?.packActivated;
        const lockedModule = formData[schoolToUpdate.udiseNumber]?.lockedModule || {}; // Get existing locked modules

        if (!salePersonName || !paidAmount || !finalPrize || !activationDate || !deactivationDate) {
          alert("Please fill all fields before activating.");
          return;
        }

        const newStatus = schoolToUpdate.userStatus === 'Deactive' ? 'Active' : 'Deactive';

        try {
          const response = await fetch(`${databaseURL}/Admin/${udiseNumber}.json`);
          const existingData = await response.json();

          const updatedSchoolData = {
            ...existingData,
            userStatus: newStatus,
            salePersonName,
            paidAmount,
            finalPrize,
            activationDate,
            deactivationDate,
            packActivated,
            lockedModule, // Maintain the locked modules
          };

          await fetch(`${databaseURL}/Admin/${udiseNumber}.json`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updatedSchoolData),
          });

          setSchools(prevSchools =>
            prevSchools.map(school =>
              school.udiseNumber === udiseNumber ? { ...school, userStatus: newStatus } : school
            )
          );

          checkDeactivationDates();
        } catch (error) {
          setError(error.message);
        }
      } else {
        alert("Incorrect passkey. Status change denied.");
      }
    }
  };



  const handleInputChange = (udiseNumber, e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [udiseNumber]: {
        ...formData[udiseNumber],
        [name]: value,
      },
    });
  };

  const handleRadioChange = (udiseNumber, e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      [udiseNumber]: {
        ...formData[udiseNumber],
        packActivated: value,
        // isFreeTaken: value === 'freeTrial' ? true : formData[udiseNumber].isFreeTaken, // Keep isFreeTaken true if free Trial is selected
      },
    });
  };

  const handleSubmit = (udiseNumber, excludePurchesPlan = false) => {
    const packActivated = formData[udiseNumber]?.packActivated;
    let lockedModule = {};

    // If packActivated is freeTrial, unlock all modules
    if (packActivated === 'freeTrial') {
      lockedModule = {};
    }
    // If a paid package is selected, use the locked modules defined in handlePackageSelection
    else if (packActivated === 'purchase') {
      lockedModule = formData[udiseNumber]?.lockedModule || {};
    }

    const dataToSave = {
      ...formData[udiseNumber],
      lockedModule: lockedModule,
      activationDate: new Date().toISOString(), // Set current date as activation date
    };

    if (excludePurchesPlan) {
      delete dataToSave.purchesPlan;
    }

    fetch(`${databaseURL}/Admin/${udiseNumber}.json`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dataToSave),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to save data');
        }
        alert("Saved Successfully");
        fetchdata();
      })
      .catch((error) => {
        setError(error.message);
      });
  };
  //9+5
  const toggleSchoolForm = (udiseNumber) => {
    setExpandedSchool(prev => (prev === udiseNumber ? null : udiseNumber));
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999
      }}>
        <img style={{ height: '100px', width: '100px' }} src={loadingimg} alt="Loading..." />
      </div>
    );
  }
  if (error) {
    return <p>Error: {error}</p>;
  }







  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated'); // or 'isLoggedIn', match your key
    navigate('/login');
  };

  return (
    <div className="main-content-of-page">



      <h2 style={{ color: 'rgb(3, 54, 94)', marginBottom: '30px' }} className="text-center">Admin Dashboard</h2>

      {/* Search Bar */}
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <input
          type="text"
          placeholder="Search by School Name, UDISE No., Phone Number, User Status, or Registration Date..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: 1,
            padding: '12px 20px',
            fontSize: '16px',
            border: '2px solid #4CAF50',
            borderRadius: '8px',
            outline: 'none',
            transition: 'border-color 0.3s',
          }}
          onFocus={(e) => e.target.style.borderColor = '#2196F3'}
          onBlur={(e) => e.target.style.borderColor = '#4CAF50'}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            style={{
              padding: '12px 20px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              transition: 'background-color 0.3s',
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
          >
            Clear
          </button>
        )}
      </div>

      <button className='btn btn-warning mb-2' onClick={() => setShowExpiryPlanList(!showExpiryPlanList)}>Show List of Expiry Plan</button>


      {showExpiryPlanList && (
        <table className="table table-bordered">
          <thead>
            <tr>
              <th style={{ backgroundColor: '#cfcfcf', border: '1px solid black' }}>School Name</th>
              <th style={{ backgroundColor: '#cfcfcf', border: '1px solid black' }}>Mobile Number</th>
              <th style={{ backgroundColor: '#cfcfcf', border: '1px solid black' }}>Deactivation Date</th>
            </tr>
          </thead>
          <tbody>
            {deactivatingUsers.map((user) => (
              <tr key={user.id}>
                <td style={{ border: '1px solid black' }}>{user.schoolName}</td>
                <td style={{ border: '1px solid black' }}>{user.mobileNumber}</td>
                <td style={{ border: '1px solid black' }}>{formData[user.udiseNumber]?.deactivationDate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}


      {schools.length === 0 ? (
        <p>No schools found in the database.</p>
      ) : (
        <div className='table-responsive'>
          <div style={{ overflowX: 'auto' }}> {/* Enable horizontal scrolling */}
            <table className='table' style={{ boxShadow: '0 4px 15px rgba(0, 0, 0, 0.5)', width: '100%', border: '1px solid black' }}>
              <thead>
                <tr>
                  <th style={{ backgroundColor: '#bcdcff', border: '1px solid black', width: '60px', padding: '10px 5px', fontWeight: 'bold' }}>Sr. No.</th>
                  <th style={{ backgroundColor: '#bcdcff', border: '1px solid black', width: '240px', fontWeight: 'bold' }}>
                    School Name
                    <button className='btn btn-secondary ms-2' onClick={() => handleSort('schoolName')} style={{ padding: '0px 5px', fontSize: '17px' }}>
                      {sort.schoolName === 'asc' ? '↑' : '↓'}
                    </button>
                  </th>
                  <th style={{ backgroundColor: '#bcdcff', border: '1px solid black', width: '100px', fontWeight: 'bold' }}>
                    UDISE No.
                    <button className='btn btn-secondary ms-2' onClick={() => handleSort('udiseNumber')} style={{ padding: '0px 5px', fontSize: '17px' }}>
                      {sort.udiseNumber === 'asc' ? '↑' : '↓'}
                    </button>
                  </th>
                  <th style={{ backgroundColor: '#bcdcff', border: '1px solid black', width: '100px', fontWeight: 'bold' }}>Phone Number</th>
                  <th style={{ backgroundColor: '#bcdcff', border: '1px solid black', width: '100px', fontWeight: 'bold' }}>
                    User Status
                    <button className='btn btn-secondary ms-2' onClick={() => handleSort('userStatus')} style={{ padding: '0px 5px', fontSize: '17px' }}>
                      {sort.userStatus === 'asc' ? '↑' : '↓'}
                    </button>
                  </th>
                  <th style={{ backgroundColor: '#bcdcff', border: '1px solid black', width: '130px', fontWeight: 'bold' }}>
                    Registration Date
                    <button className='btn btn-secondary ms-2' onClick={() => handleSort('registrationDate')} style={{ padding: '0px 5px', fontSize: '17px' }}>
                      {sort.registrationDate === 'asc' ? '↑' : '↓'}
                    </button>
                  </th>
                  <th style={{ backgroundColor: '#bcdcff', border: '1px solid black', fontWeight: 'bold', width: '380px' }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredSchools.map((school, index) => (
                  <React.Fragment key={school.id}>
                    <tr style={{ backgroundColor: school.userStatus === 'Active' ? '#00bcf954' : '' }}>
                      <td style={{ width: '60px', fontWeight: 'bold' }}>
                        {index + 1}
                      </td>
                      <td style={{ border: '1px solid black' }}>{school.schoolName}</td>
                      <td style={{ border: '1px solid black' }}>{school.udiseNumber}</td>
                      <td style={{ border: '1px solid black' }}>{school.mobileNumber}</td>
                      <td style={{ border: '1px solid black' }}>{school.userStatus}</td>
                      <td style={{ border: '1px solid black' }}>{school.registerDate}</td>
                      <td style={{ width: '380px', border: '1px solid black' }}>
                        <div style={{ fontWeight: 'bold', cursor: 'pointer', right: 0, float: 'right', marginRight: '30px' }} onClick={() => toggleSchoolForm(school.udiseNumber)}>
                          {expandedSchool === school.udiseNumber ? '▼' : ' ▲'}
                        </div>

                        {expandedSchool === school.udiseNumber && (
                          <div>
                            <div style={{ padding: '10px' }}>
                              <div style={{ textAlign: 'left', marginBottom: '5px' }}>
                                Sales Person: <input
                                  type="text"
                                  name="salePersonName"
                                  placeholder="Sales Person Name"
                                  value={formData[school.udiseNumber]?.salePersonName || ''}
                                  onChange={(e) => handleInputChange(school.udiseNumber, e)} style={{ width: '180px', marginLeft: '44px' }}
                                />
                              </div>
                              <div style={{ textAlign: 'left', marginBottom: '5px' }}>
                                Pay Amount: <input
                                  type="number"
                                  name="paidAmount"
                                  placeholder="Pay Amount"
                                  value={formData[school.udiseNumber]?.paidAmount || ''}
                                  onChange={(e) => handleInputChange(school.udiseNumber, e)} style={{ width: '180px', marginLeft: '46px' }}
                                />
                              </div>
                              <div style={{ textAlign: 'left', marginBottom: '5px' }}>
                                Final Amount: <input
                                  type="number"
                                  name="finalPrize"
                                  placeholder="Final Amount"
                                  value={formData[school.udiseNumber]?.finalPrize || ''}
                                  onChange={(e) => handleInputChange(school.udiseNumber, e)} style={{ width: '180px', marginLeft: '38px' }}
                                />
                              </div>
                              <div style={{ textAlign: 'left', marginBottom: '5px' }}>
                                Activation Date: <input
                                  type="text"
                                  name="activationDate"
                                  value={new Date().toISOString().split('T')[0]}
                                  readOnly
                                  style={{ width: '180px', marginLeft: '23.6px' }}
                                />
                              </div>
                              <div style={{ textAlign: 'left', marginBottom: '10px' }}>
                                Deactivation Date: <input
                                  type="date"
                                  name="deactivationDate"
                                  value={formData[school.udiseNumber]?.deactivationDate || ''}
                                  disabled
                                  //   onChange={(e) => handleInputChange(school.udiseNumber, e)} 
                                  style={{ width: '180px', marginLeft: '6px', color: 'black' }}
                                />
                              </div>





                              <div style={{ textAlign: 'left', marginBottom: '5px' }}>
                                Activate: <label>
                                  <input
                                    type="radio"
                                    value="freeTrial"
                                    checked={formData[school.udiseNumber]?.packActivated === 'freeTrial'}
                                    onChange={(e) => handleRadioChange(school.udiseNumber, e)}
                                    style={{ width: '18px', marginLeft: '20px' }}
                                    disabled={formData[school.udiseNumber]?.isFreeTaken}
                                  />
                                  Free Trial
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    value="purchase"
                                    checked={formData[school.udiseNumber]?.packActivated === 'purchase'}
                                    onChange={(e) => {
                                      handleRadioChange(school.udiseNumber, e);
                                      setIsModalOpen(true); // Open modal when Purchase Basic package is selected
                                    }}
                                    style={{ width: '18px', marginLeft: '20px' }}
                                  />
                                  Purchase Basic package
                                </label>
                              </div>



                              {formData[school.udiseNumber]?.packActivated === 'freeTrial' && (
                                <div>
                                  <button onClick={() => handleFreePackageSelection(school.udiseNumber)}
                                    style={{
                                      background: 'linear-gradient(45deg,rgba(184, 36, 26, 0.66),rgb(212, 192, 4))', // Golden gradient
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '5px',
                                      padding: '3px 8px',
                                      margin: '5px',
                                      cursor: 'pointer',
                                      fontSize: '16px',
                                      fontWeight: 'bold',
                                      transition: 'background 0.3s, transform 0.3s',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.transform = 'scale(1.04)'; // Slightly enlarge on hover
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.transform = 'scale(1)'; // Return to original size
                                    }}>{FREE_TRIAL_PLAN.durationDays} Days - Up to {FREE_TRIAL_PLAN.studentLimit} Students</button>
                                </div>
                              )}

                              {formData[school.udiseNumber]?.packActivated === 'purchase' && (
                                <div>
                                  {PAID_PLANS.map((plan) => (
                                    <button
                                      key={plan.name}
                                      style={{
                                        background: formData[school.udiseNumber]?.planName === plan.name
                                          ? 'linear-gradient(45deg, #4CAF50, #8BC34A)'
                                          : 'linear-gradient(45deg,rgb(255, 106, 193),rgb(255, 44, 44))',
                                        color: 'white',
                                        border: formData[school.udiseNumber]?.planName === plan.name ? '2px solid yellow' : 'none',
                                        borderRadius: '5px',
                                        padding: '3px 8px',
                                        margin: '5px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                      }}
                                      onClick={() => handlePackageSelection(school.udiseNumber, plan.name)}
                                    >
                                      {plan.name} (Up to {plan.studentLimit.toLocaleString()} students, ₹{Number(plansData[plan.name]?.mrp ?? plan.price).toLocaleString()} / year)
                                    </button>
                                  ))}
                                </div>
                              )}


                              {isModalOpen && (
                                <Modal>
                                  <h2>Subscription Plans</h2>
                                  <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px" }}>
                                    <thead>
                                      <tr>
                                        <th style={{ background: "#4CAF50", color: "white", padding: "12px", border: "1px solid #ddd" }}>Plan</th>
                                        <th style={{ background: "#4CAF50", color: "white", padding: "12px", border: "1px solid #ddd" }}>Student Limit</th>
                                        <th style={{ background: "#4CAF50", color: "white", padding: "12px", border: "1px solid #ddd" }}>Price (₹)</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {Object.entries(plansData).map(([planName, planData]) => (
                                        <tr key={planName}>
                                          <td style={{
                                            padding: "10px",
                                            border: "1px solid #ddd",
                                            textAlign: "center",
                                            fontWeight: "bold",
                                            background: planName === "Free Trial" ? "#f8f9fa" : "white"
                                          }}>
                                            {planName}
                                          </td>
                                          <td style={{ padding: "10px", border: "1px solid #ddd", textAlign: "center" }}>
                                            {planData.studentLimit?.toLocaleString()} students
                                          </td>
                                          <td style={{ padding: "10px", border: "1px solid #ddd", textAlign: "center" }}>
                                            <input
                                              type="number"
                                              min="0"
                                              value={planData.mrp}
                                              disabled={planName === FREE_TRIAL_PLAN.name}
                                              onChange={(e) => {
                                                setPlansData((prevPlansData) => ({
                                                  ...prevPlansData,
                                                  [planName]: {
                                                    ...prevPlansData[planName],
                                                    mrp: e.target.value
                                                  },
                                                }));
                                              }}
                                              style={{
                                                width: "90%",
                                                padding: "8px",
                                                border: "1px solid #ccc",
                                                borderRadius: "4px",
                                                outline: "none",
                                                textAlign: "center"
                                              }}
                                            />
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>



                                  {formData[expandedSchool]?.userStatus === 'Active' ? (
                                    <p style={{ color: 'red', marginTop: "15px" }}>Plan cannot be changed after activation.</p>
                                  ) : (
                                    <div style={{ display: "flex", justifyContent: "center", gap: "15px", marginTop: "20px" }}>
                                      <button
                                        onClick={() => {
                                          const udiseNumber = expandedSchool;
                                          const dataToSave = {
                                            purchesPlan: plansData,
                                          };
                                          fetch(`${databaseURL}/Admin/${udiseNumber}.json`, {
                                            method: 'PATCH',
                                            headers: {
                                              'Content-Type': 'application/json',
                                            },
                                            body: JSON.stringify(dataToSave),
                                          })
                                            .then((response) => {
                                              if (!response.ok) {
                                                throw new Error('Failed to save data');
                                              }
                                              alert("Plan data saved successfully");
                                              setIsModalOpen(false);
                                            })
                                            .catch((error) => {
                                              setError(error.message);
                                            });
                                        }}
                                        style={{
                                          padding: "10px 25px",
                                          background: "#4CAF50",
                                          color: "white",
                                          border: "none",
                                          borderRadius: "5px",
                                          cursor: "pointer",
                                          fontSize: "16px",
                                          fontWeight: "bold"
                                        }}
                                      >
                                        Save Plans
                                      </button>

                                    </div>
                                  )}

                                  <button
                                    onClick={() => setIsModalOpen(false)}
                                    style={{ marginLeft: "10px", padding: "10px 15px", background: "red", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}
                                  >
                                    Close
                                  </button>

                                </Modal>
                              )}

                              <div style={{ width: '380px', textAlign: 'left', padding: '10px', paddingBottom: '0px' }}>
                                <span style={{ color: 'brown' }}>Be Careful While Clicking Button:</span>
                                <button
                                  onClick={() => handleToggleStatus(school.udiseNumber)}
                                  style={{
                                    backgroundColor: school.userStatus === 'Deactive' ? 'green' : 'red',
                                    color: 'white',
                                    border: 'none',
                                    padding: '3px 7px',
                                    cursor: 'pointer',
                                    borderRadius: '5px', marginBottom: '10px', fontSize: '15px', marginLeft: '5px'
                                  }}
                                >
                                  {school.userStatus === 'Deactive' ? 'Activate' : 'Deactivate'}
                                </button>
                              </div>
                              <hr />

                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <style jsx>{`
            .table td {
              white-space: normal;
            }
           th{
           border:1px soild black;
            }
            .main-content-of-page {
   margin:auto;
    width: 95%;
    padding: 1%;
}
          `}</style>
        </div>
      )}
    </div>
  );
}

export default ActivationPlan;